import { Crown, MoreHorizontal, UserMinus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import type { Workspace } from "@/hooks/useWorkspace";
import { STATUS_LABEL, type MemberRole, type MemberRow } from "./useWorkspaceMembers";

type Props = {
  workspace: Workspace;
  loading: boolean;
  activeMembers: MemberRow[];
  revokedMembers: MemberRow[];
  displayMap: Record<string, { display_name: string | null; email: string | null }>;
  busyId: string | null;
  currentUserId: string | undefined;
  onUpdateRole: (m: MemberRow, role: MemberRole) => void;
  onRevoke: (m: MemberRow) => void;
  onRequestTransfer: (m: MemberRow) => void;
};

export const MembersList = ({
  workspace, loading, activeMembers, revokedMembers, displayMap,
  busyId, currentUserId, onUpdateRole, onRevoke, onRequestTransfer,
}: Props) => {
  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  return (
    <>
      {activeMembers.length === 0 ? (
        <p className="text-sm text-muted-foreground">No team members yet.</p>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border">
          {activeMembers.map((m) => {
            const info = displayMap[m.member_user_id];
            const primary = info?.display_name || info?.email || m.member_user_id;
            const secondary = info?.display_name && info?.email ? info.email : null;
            return (
              <li key={m.id} className="flex flex-wrap items-center justify-between gap-3 px-3 py-2">
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-foreground">{primary}</span>
                    <Badge variant="secondary" className="text-[10px]">{STATUS_LABEL[m.status]}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {secondary ? `${secondary} · ` : ""}
                    {m.accepted_at
                      ? `Joined ${new Date(m.accepted_at).toLocaleDateString()}`
                      : `Invited ${new Date(m.invited_at).toLocaleDateString()}`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Select
                    value={m.role}
                    onValueChange={(v) => onUpdateRole(m, v as MemberRole)}
                    disabled={busyId === m.id}
                  >
                    <SelectTrigger className="h-9 w-[160px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="owner_admin">Owner / admin</SelectItem>
                      <SelectItem value="editor">Editor</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => onRevoke(m)}
                    disabled={busyId === m.id}
                    aria-label="Revoke access"
                  >
                    <UserMinus className="h-4 w-4" />
                  </Button>
                  {!workspace.is_primary && m.member_user_id !== currentUserId && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" aria-label="More actions">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onRequestTransfer(m)}>
                          <Crown className="h-4 w-4" /> Transfer ownership
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {revokedMembers.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-muted-foreground">
            Revoked ({revokedMembers.length})
          </summary>
          <ul className="mt-2 divide-y divide-border rounded-md border border-border">
            {revokedMembers.map((m) => (
              <li key={m.id} className="flex items-center justify-between px-3 py-2">
                <span className="truncate text-sm text-muted-foreground">
                  {displayMap[m.member_user_id]?.display_name || displayMap[m.member_user_id]?.email || m.member_user_id}
                </span>
                <span className="text-xs text-muted-foreground">
                  Revoked {m.revoked_at ? new Date(m.revoked_at).toLocaleDateString() : "—"}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  );
};
